export const siteConfig = {
  brandName: "pningwillow",
  logo: "/head/pningwillow-wordmark.svg",
  mark: "/head/pningwillow-mark.svg",
  description:
    "Considered clothing, accessories and home textiles for everyday life outdoors and at home.",
  announcement:
    "Made for the long run — tracked delivery for $9.99",
  contact: {
    email: "request@pningwillow.xyz",
    address: "pningwillow Studio · Hong Kong SAR",
    hours: "Monday–Friday, 09:00–17:00 HKT",
  },
  currency: "USD",
  locale: "en-US",
  shipping: {
    standard: 9.99,
    freeThreshold: Number.POSITIVE_INFINITY,
    deliveryWindow: "5–10 working days",
  },
};

export const siteMedia = {
  hero: {
    desktop: "/main/hero-desktop.webp",
    mobile: "/main/hero-mobile.webp",
  },
  workshop: "/main/story-workshop.webp",
  forest: "/main/story-forest.webp",
  home: "/main/home-textile.webp",
};

export const formatCurrency = (value) =>
  new Intl.NumberFormat(siteConfig.locale, {
    style: "currency",
    currency: siteConfig.currency,
  }).format(Number(value));

const product = (
  id,
  name,
  category,
  price,
  color,
  material,
  sizes,
  description,
  tags,
  extra = {}
) => ({
  id,
  name,
  category,
  price,
  image: `/img/products/product-${String(id).padStart(2, "0")}.${extra.imageExt || "webp"}`,
  images: [
    `/img/products/product-${String(id).padStart(2, "0")}.${extra.imageExt || "webp"}`,
    `/img/products/product-${String(id).padStart(2, "0")}-detail.${extra.imageExt || "webp"}`,
  ],
  description: `${description} Thoughtful proportions, reinforced stress points and clear care guidance make it dependable for frequent use. Each piece is checked for finish and designed to settle naturally with wear rather than feel disposable.`,
  attributes: {
    Material: material,
    Origin: "Designed and quality-checked in Hong Kong",
    Production: "Small-batch, demand-led",
    Care: "Wash cool, air dry, wear often",
  },
  tags,
  colors: [color],
  sizes,
  rating: extra.rating || 4.8,
  reviews: extra.reviews || 24 + id * 3,
  stock: extra.stock || 12 + id,
  sku: `PC-${String(id).padStart(3, "0")}`,
  badge: extra.badge || "",
  featured: Boolean(extra.featured),
  soldOut: Boolean(extra.soldOut),
});

const productData = [
  product(
    1,
    "Alpine Loop Organic Tee",
    "Tops",
    49.9,
    "Sage",
    "100% GOTS organic cotton, 190 g/m²",
    ["XS", "S", "M", "L", "XL", "XXL"],
    "A softly structured everyday tee inspired by the contour lines of quiet mountain paths. The compact jersey feels substantial without losing breathability.",
    ["organic", "everyday", "unisex", "tops"],
    { featured: true, badge: "New" }
  ),
  product(
    2,
    "Quiet Ridge Heavy Tee",
    "Tops",
    52,
    "Charcoal",
    "Organic cotton compact jersey, 220 g/m²",
    ["XS", "S", "M", "L", "XL"],
    "A clean, graphic-free T-shirt with a relaxed shoulder and a dense, dry hand. Cut for repeat wear and designed to keep its shape.",
    ["organic", "heavyweight", "minimal", "tops"],
    { featured: true }
  ),
  product(
    3,
    "Forest Air Active Tee",
    "Tops",
    59,
    "Deep Navy",
    "Regenerated wood-cellulose performance jersey",
    ["XS", "S", "M", "L", "XL", "XXL"],
    "A naturally breathable performance layer for warm trails and long journeys. The smooth wood-fibre knit moves moisture without a synthetic feel.",
    ["wood fibre", "active", "breathable", "tops"],
    { featured: true, badge: "Material lab", rating: 4.9 }
  ),
  product(
    4,
    "Fern Trail Sleeveless Top",
    "Tops",
    45,
    "Natural White",
    "100% organic cotton rib jersey",
    ["XS", "S", "M", "L", "XL"],
    "A minimal sleeveless layer with a neat bound neckline and an easy straight fit. Light enough for summer, substantial enough to wear alone.",
    ["organic", "summer", "layering", "tops"],
    { badge: "New" }
  ),
  product(
    5,
    "Northwood Merino Longsleeve",
    "Wool",
    125,
    "Pine",
    "Traceable, mulesing-free merino wool",
    ["XS", "S", "M", "L", "XL"],
    "A temperature-balancing merino base layer with a soft, close-knit finish. Built for cold starts, changing weather and compact packing.",
    ["merino", "traceable", "base layer", "wool"],
    { featured: true, rating: 4.9 }
  ),
  product(
    6,
    "Hearthline Wool Overshirt",
    "Wool",
    168,
    "Rust",
    "Recycled wool blend with corozo buttons",
    ["S", "M", "L", "XL", "XXL"],
    "A warm overshirt woven from recovered wool fibres. Roomy patch pockets and a calm, boxy cut make it an adaptable outer layer.",
    ["recycled", "overshirt", "warm", "wool"],
    { badge: "Limited" }
  ),
  product(
    7,
    "Ridgewalk Recycled Shell",
    "Outerwear",
    179,
    "Moss",
    "Recycled polyamide shell with bio-based membrane",
    ["XS", "S", "M", "L", "XL"],
    "A quiet, weather-ready shell with articulated sleeves and a clean hood. Designed for daily rides, damp forests and exposed ridgelines.",
    ["recycled", "weatherproof", "shell", "outerwear"],
    { badge: "New", rating: 4.9 }
  ),
  product(
    8,
    "All-Weather Field Shirt",
    "Tops",
    89,
    "Sand",
    "Organic cotton and hemp twill",
    ["XS", "S", "M", "L", "XL"],
    "A breathable field shirt with useful chest pockets and softened twill construction. Wear it buttoned, open or as a light overshirt.",
    ["hemp", "organic", "utility", "tops"]
  ),
  product(
    9,
    "Traverse Flex Trail Pants",
    "Pants",
    99,
    "Moss",
    "Recycled stretch nylon with PFC-free finish",
    ["XS", "S", "M", "L", "XL"],
    "Light trail trousers with an articulated knee, low-profile pockets and enough stretch for steep days. Quick drying and easy to pack.",
    ["recycled", "stretch", "trail", "pants"],
    { featured: true }
  ),
  product(
    10,
    "Ochre Trail Pants",
    "Pants",
    104,
    "Terracotta",
    "Recycled stretch weave",
    ["XS", "S", "M", "L", "XL"],
    "An earthy variation of our lightweight trail trouser, cut with a tapered leg and comfortable elasticated waist for travel and movement.",
    ["recycled", "travel", "quick dry", "pants"],
    { badge: "New" }
  ),
  product(
    11,
    "Daybreak Adventure Shorts",
    "Pants",
    79,
    "Midnight Navy",
    "Recycled stretch nylon",
    ["XS", "S", "M", "L", "XL"],
    "Trail-to-water shorts with a soft inner waistband, secure pocket and fast-drying fabric. Purposeful without looking technical.",
    ["recycled", "shorts", "quick dry", "pants"]
  ),
  product(
    12,
    "Riverstone Utility Trousers",
    "Pants",
    119,
    "Slate",
    "Organic cotton canvas with recycled elastane",
    ["XS", "S", "M", "L", "XL"],
    "Durable workwear trousers refined for everyday use. A relaxed top block, subtle utility pockets and a tapered hem keep the silhouette clean.",
    ["organic", "utility", "durable", "pants"],
    { rating: 4.7 }
  ),
  product(
    13,
    "Spruce Jacquard Blanket",
    "Home & Living",
    129.9,
    "Forest / Natural",
    "100% GOTS organic cotton jacquard",
    ["140 × 200 cm"],
    "A reversible woven blanket with a restrained spruce rhythm and softly twisted fringe. Warm, breathable and produced by a family-run mill.",
    ["organic", "jacquard", "blanket", "home"],
    { featured: true, badge: "Local" }
  ),
  product(
    14,
    "Moorland Woven Blanket",
    "Home & Living",
    129.9,
    "Berry",
    "100% organic cotton double weave",
    ["140 × 200 cm"],
    "A deeply coloured double-woven blanket with a calm geometric texture. Made to add warmth to beds, sofas and nights outdoors.",
    ["organic", "woven", "blanket", "home"]
  ),
  product(
    15,
    "Canopy Organic Bedding Set",
    "Home & Living",
    154.9,
    "Undyed Cream",
    "GOTS organic cotton jacquard",
    ["135 × 200 cm", "155 × 220 cm", "200 × 200 cm"],
    "Naturally breathable bedding woven and sewn after you order. The subtle canopy texture brings depth without prints or unnecessary finishing.",
    ["organic", "bedding", "made to order", "home"],
    { badge: "Made on demand", rating: 4.9 }
  ),
  product(
    16,
    "Cloudwood Pillow Cover",
    "Home & Living",
    45,
    "Mist Grey",
    "GOTS organic cotton jacquard",
    ["40 × 80 cm", "80 × 80 cm"],
    "A smooth, breathable pillow cover with concealed zip and a subtle woven tree texture. Yarn-dyed for lasting, gentle colour.",
    ["organic", "pillow", "jacquard", "home"]
  ),
  product(
    17,
    "Contour Forestfibre Towel",
    "Home & Living",
    49,
    "Mint",
    "Wood-cellulose terry jacquard",
    ["80 × 160 cm"],
    "A highly absorbent travel towel with a three-dimensional contour weave. It dries quickly, packs small and feels soft against the skin.",
    ["wood fibre", "travel", "towel", "home"],
    { badge: "New" }
  ),
  product(
    18,
    "Pinecrest Travel Towel",
    "Home & Living",
    45,
    "Lake Blue",
    "Lyocell and organic cotton weave",
    ["70 × 140 cm"],
    "A compact, fast-drying towel for swimming, camping and small bathrooms. A tonal topographic weave gives it a tactile surface.",
    ["lyocell", "travel", "towel", "home"]
  ),
  product(
    19,
    "Trailmark Organic Socks",
    "Accessories",
    45,
    "Cream / Fern",
    "Organic cotton with recycled polyamide reinforcement",
    ["35–38", "39–42", "43–46"],
    "Cushioned crew socks with breathable rib zones and a clean colour block. Reinforced where needed for long walks and daily wear.",
    ["organic", "socks", "cushioned", "accessories"],
    { featured: true }
  ),
  product(
    20,
    "Twin Pines Sport Socks",
    "Accessories",
    45,
    "Charcoal / Natural",
    "Organic cotton performance knit",
    ["35–38", "39–42", "43–46"],
    "A firmer sport sock with a supportive mid-foot band and soft looped sole. Built for trainers, boots and cool morning starts.",
    ["organic", "sport", "socks", "accessories"]
  ),
  product(
    21,
    "Timberline Rib Beanie",
    "Accessories",
    49.9,
    "Moss",
    "Mulesing-free merino wool",
    ["One size"],
    "A soft rib-knit beanie with a low-profile crown and adjustable fold. Warm without bulk and comfortable directly against the skin.",
    ["merino", "beanie", "winter", "accessories"]
  ),
  product(
    22,
    "Summit Fold Beanie",
    "Accessories",
    52,
    "Berry",
    "Recycled wool and merino blend",
    ["One size"],
    "A substantial two-tone knit hat shaped for windy platforms, forest camps and everyday winter routines.",
    ["recycled", "wool", "beanie", "accessories"],
    { badge: "Limited" }
  ),
  product(
    23,
    "Grove Canvas Cap",
    "Accessories",
    49,
    "Stone",
    "Organic cotton canvas",
    ["One size · adjustable"],
    "An unstructured six-panel cap in washed organic canvas. The shallow crown and metal adjuster create an easy, familiar fit.",
    ["organic", "cap", "canvas", "accessories"]
  ),
  product(
    24,
    "Forestfield Neck Gaiter",
    "Accessories",
    45,
    "Night Navy",
    "Traceable merino jersey",
    ["One size"],
    "A versatile merino tube for cold ridges and changeable commutes. Wear it as a neck layer, headband or light hat.",
    ["merino", "multifunctional", "winter", "accessories"],
    { soldOut: true }
  ),
  product(
    25,
    "Harbour Fold Utility Tote",
    "Accessories",
    68,
    "Olive",
    "Washed organic cotton canvas with natural cotton lining",
    ["One size · 42 × 32 × 14 cm"],
    "A softly structured carry-all with reinforced handles, an easy snap closure and practical internal and external pockets. The wide base keeps daily essentials organised while the washed canvas gives the bag a familiar, naturally relaxed character.",
    ["organic", "canvas", "tote", "accessories"],
    { featured: true, badge: "New", imageExt: "png", rating: 4.8 }
  ),
];

export const productCategories = [
  "All products",
  "Tops",
  "Pants",
  "Wool",
  "Outerwear",
  "Home & Living",
  "Accessories",
];

export default productData;
